/**
 * W3 — testable mirrors of RouteCreateForm Group UI cargo query + save boundaries.
 */
import type { PhysicalCargoSourceRow } from '../../types/physicalCargoSource';
import { buildAvailableCargoQueryParams } from '../../components/routes/RouteCreateForm/cp060RouteCreateStateMachine';
import { resolveCargoQueryFromLoadingStop } from './executionBindingHelpers';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import {
  GROUP_MISSING_PHYSICAL_SOURCE_MESSAGE,
  isGroupPrimaryPhysicalSourceReady,
  primaryLoadingStopMatchesPhysicalSourcesList
} from './groupPhysicalSourceFlow';
import {
  resolveActiveLoadingStopByUid,
  type LoadingExecutionContext
} from './step4ExecutionState';

export const GROUP_PHYSICAL_SOURCES_PENDING_MESSAGE =
  'Подождите: загружаются физические источники CP-059 для выбранной заявки…';

export const GROUP_PHYSICAL_SOURCES_NOT_MEMBER_MESSAGE =
  'Выбранный физический источник недоступен в текущем списке CP-059. Выберите источник заново.';

/** Snapshot of RTK `getPhysicalCargoSources` for one applicationId argument. */
export type GroupUiPhysicalSourcesQuerySnapshot = {
  requestedApplicationId: number;
  excludeRouteId?: number;
  isUninitialized?: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  data?: { applicationId?: number; sources?: PhysicalCargoSourceRow[] } | null;
};

export type GroupUiPhysicalSourceMembership =
  | { permitted: true }
  | {
      permitted: false;
      code: 'structural' | 'no_query' | 'pending' | 'error' | 'stale' | 'not_member';
      message: string;
    };

export function evaluateGroupUiPhysicalSourceMembership(params: {
  group: DeliveryGroupDraft | null;
  query: GroupUiPhysicalSourcesQuerySnapshot | null;
}): GroupUiPhysicalSourceMembership {
  if (!params.group) {
    return {
      permitted: false,
      code: 'structural',
      message: GROUP_MISSING_PHYSICAL_SOURCE_MESSAGE
    };
  }
  if (!isGroupPrimaryPhysicalSourceReady(params.group)) {
    return {
      permitted: false,
      code: 'structural',
      message: GROUP_MISSING_PHYSICAL_SOURCE_MESSAGE
    };
  }
  if (!params.query) {
    return {
      permitted: false,
      code: 'no_query',
      message: GROUP_PHYSICAL_SOURCES_PENDING_MESSAGE
    };
  }

  const groupAppId = Number(params.group.applicationId);
  if (params.query.requestedApplicationId !== groupAppId) {
    return {
      permitted: false,
      code: 'stale',
      message: GROUP_PHYSICAL_SOURCES_NOT_MEMBER_MESSAGE
    };
  }

  if (
    params.query.isUninitialized ||
    params.query.isLoading ||
    params.query.isFetching ||
    (!params.query.isSuccess && !params.query.isError)
  ) {
    return {
      permitted: false,
      code: 'pending',
      message: GROUP_PHYSICAL_SOURCES_PENDING_MESSAGE
    };
  }

  if (params.query.isError) {
    return {
      permitted: false,
      code: 'error',
      message: 'Не удалось загрузить физические источники CP-059 для заявки группы.'
    };
  }

  const responseAppId = params.query.data?.applicationId;
  if (responseAppId != null && Number(responseAppId) !== groupAppId) {
    return {
      permitted: false,
      code: 'stale',
      message: GROUP_PHYSICAL_SOURCES_NOT_MEMBER_MESSAGE
    };
  }

  const sources = params.query.data?.sources ?? [];
  if (!primaryLoadingStopMatchesPhysicalSourcesList(params.group, sources)) {
    return {
      permitted: false,
      code: 'not_member',
      message: GROUP_PHYSICAL_SOURCES_NOT_MEMBER_MESSAGE
    };
  }

  return { permitted: true };
}

export function resolveGroupUiComputedCargoSource(params: {
  activeGroup: DeliveryGroupDraft | null;
  activeLoadingContext: LoadingExecutionContext | null;
}): { loadingPointType?: 'warehouse' | 'application'; loadingPointId?: number } {
  if (!params.activeGroup || !params.activeLoadingContext) {
    return {};
  }
  const loadingStop = resolveActiveLoadingStopByUid(
    params.activeGroup,
    params.activeLoadingContext.stopUid
  );
  return resolveCargoQueryFromLoadingStop(loadingStop, params.activeGroup);
}

export function isGroupUiCargoQueryPermitted(params: {
  groupUiEnabled: boolean;
  activeGroup: DeliveryGroupDraft | null;
  physicalSourcesQuery: GroupUiPhysicalSourcesQuerySnapshot | null;
}): boolean {
  if (!params.groupUiEnabled) return true;
  return evaluateGroupUiPhysicalSourceMembership({
    group: params.activeGroup,
    query: params.physicalSourcesQuery
  }).permitted;
}

/** Same skip condition as RouteCreateForm `useGetAvailableCargoQuery` for Group UI. */
export function buildGroupUiAvailableCargoQueryParams(params: {
  groupUiEnabled: boolean;
  activeGroup: DeliveryGroupDraft | null;
  activeLoadingContext: LoadingExecutionContext | null;
  excludeRouteId?: number;
  physicalSourcesQuery: GroupUiPhysicalSourcesQuerySnapshot | null;
}): ReturnType<typeof buildAvailableCargoQueryParams> | null {
  if (
    !isGroupUiCargoQueryPermitted({
      groupUiEnabled: params.groupUiEnabled,
      activeGroup: params.activeGroup,
      physicalSourcesQuery: params.physicalSourcesQuery
    })
  ) {
    return null;
  }
  const source = resolveGroupUiComputedCargoSource(params);
  if (!source.loadingPointType || source.loadingPointId == null) {
    return null;
  }
  return buildAvailableCargoQueryParams({
    applicationId:
      params.activeGroup?.applicationId != null
        ? Number(params.activeGroup.applicationId)
        : null,
    loadingPointType: source.loadingPointType,
    loadingPointId: source.loadingPointId,
    excludeRouteId: params.excludeRouteId
  });
}

export function collectApplicationIdsFromDeliveryGroups(groups: DeliveryGroupDraft[]): number[] {
  const ids = new Set<number>();
  for (const g of groups) {
    const appId = g.applicationId != null ? Number(g.applicationId) : NaN;
    if (Number.isFinite(appId) && appId > 0) ids.add(appId);
  }
  return Array.from(ids).sort((a, b) => a - b);
}

export function resolveRouteCreateApplicationIdsFromGroups(params: {
  groups: DeliveryGroupDraft[];
  selectedCargoRows: Array<{ id: number; applicationId?: number; application?: { id?: number } }>;
}): number[] {
  const ids = new Set(collectApplicationIdsFromDeliveryGroups(params.groups));
  for (const row of params.selectedCargoRows) {
    const aid = Number(row.applicationId ?? row.application?.id);
    if (Number.isFinite(aid) && aid > 0) ids.add(aid);
  }
  return Array.from(ids).sort((a, b) => a - b);
}

export function buildGroupUiPhysicalSourcesQuerySnapshot(params: {
  requestedApplicationId: number;
  excludeRouteId?: number;
  slice: {
    isUninitialized?: boolean;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
    data?: { applicationId?: number; sources?: PhysicalCargoSourceRow[] } | null;
  };
}): GroupUiPhysicalSourcesQuerySnapshot {
  return {
    requestedApplicationId: params.requestedApplicationId,
    excludeRouteId: params.excludeRouteId,
    isUninitialized: params.slice.isUninitialized,
    isLoading: params.slice.isLoading,
    isFetching: params.slice.isFetching,
    isError: params.slice.isError,
    isSuccess: params.slice.isSuccess,
    data: params.slice.data ?? null
  };
}

/** RTK Query cache key slice — different active groups must not share params. */
export function groupUiCargoQueryCacheKey(params: ReturnType<typeof buildAvailableCargoQueryParams>): string {
  if (!params) return '';
  return `${params.loadingPointType}|${params.loadingPointId}|${params.excludeRouteId ?? ''}`;
}
